<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { gsap } from 'gsap';

  export let enabled = true;
  export let glowColor = '255, 255, 255';
  export let spotlightRadius = 300;

  let spotlightEl: HTMLDivElement;
  let sectionEl: HTMLElement | null = null;

  function updateCardGlow(card: HTMLElement, mouseX: number, mouseY: number, intensity: number) {
    const rect = card.getBoundingClientRect();
    const relativeX = ((mouseX - rect.left) / rect.width) * 100;
    const relativeY = ((mouseY - rect.top) / rect.height) * 100;

    card.style.setProperty('--glow-x', `${relativeX}%`);
    card.style.setProperty('--glow-y', `${relativeY}%`);
    card.style.setProperty('--glow-intensity', intensity.toString());
  }

  function handleMouseMove(e: MouseEvent) {
    if (!enabled || !spotlightEl) return;

    const section = sectionEl || document.querySelector('#pricing');
    if (!section) return;

    const rect = section.getBoundingClientRect();
    const mouseInside =
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;

    if (!mouseInside) {
      gsap.to(spotlightEl, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out'
      });

      const cards = section.querySelectorAll('.magic-bento-card');
      cards.forEach(card => {
        (card as HTMLElement).style.setProperty('--glow-intensity', '0');
      });
      return;
    }

    const proximity = spotlightRadius * 0.5;
    const fadeDistance = spotlightRadius * 0.75;
    let minDistance = Infinity;

    const cards = section.querySelectorAll('.magic-bento-card');
    cards.forEach(card => {
      const cardElement = card as HTMLElement;
      const cardRect = cardElement.getBoundingClientRect();
      const centerX = cardRect.left + cardRect.width / 2;
      const centerY = cardRect.top + cardRect.height / 2;
      const distance = Math.hypot(e.clientX - centerX, e.clientY - centerY) -
        Math.max(cardRect.width, cardRect.height) / 2;
      const effectiveDistance = Math.max(0, distance);

      minDistance = Math.min(minDistance, effectiveDistance);

      let glowIntensity = 0;
      if (effectiveDistance <= proximity) {
        glowIntensity = 1;
      } else if (effectiveDistance <= fadeDistance) {
        glowIntensity = (fadeDistance - effectiveDistance) / (fadeDistance - proximity);
      }

      updateCardGlow(cardElement, e.clientX, e.clientY, glowIntensity);
    });

    gsap.to(spotlightEl, {
      left: e.clientX,
      top: e.clientY,
      duration: 0.1,
      ease: 'power2.out'
    });

    const targetOpacity = minDistance <= proximity
      ? 0.5
      : minDistance <= fadeDistance
        ? ((fadeDistance - minDistance) / (fadeDistance - proximity)) * 0.5
        : 0;

    gsap.to(spotlightEl, {
      opacity: targetOpacity,
      duration: targetOpacity > 0 ? 0.2 : 0.5,
      ease: 'power2.out'
    });
  }

  onMount(() => {
    // Disable on mobile for performance
    const isMobile = window.innerWidth <= 768;
    if (isMobile || !enabled) return;

    sectionEl = document.querySelector('#pricing');
    document.addEventListener('mousemove', handleMouseMove);
  });

  onDestroy(() => {
    document.removeEventListener('mousemove', handleMouseMove);
  });
</script>

{#if enabled}
  <div
    bind:this={spotlightEl}
    class="fixed pointer-events-none z-50"
    style="
      width: 800px;
      height: 800px;
      border-radius: 50%;
      background: radial-gradient(circle,
        rgba({glowColor}, 0.08) 0%,
        rgba({glowColor}, 0.04) 15%,
        rgba({glowColor}, 0.02) 25%,
        rgba({glowColor}, 0.01) 40%,
        transparent 60%
      );
      opacity: 0;
      transform: translate(-50%, -50%);
      mix-blend-mode: screen;
    "
  />
{/if}
